import React, {useEffect, useState} from 'react';
import isEqual from 'react-fast-compare';
import {connect} from 'react-redux';

import * as Analytics from 'analytics/Analytics';
import * as SampleConstants from 'constants/SampleConstants';
import {Container} from 'constants/SampleConstants';
import {SampleStatusItem} from 'constants/SampleStatusConstants';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  checkSamplesForStatusWarnings,
  SampleStatusWarning,
} from 'inventory/sample-status/SampleStatusWarning';
import {BaseSampleStatusWarningTrackingParams} from 'inventory/sample-status/SampleStatusWarningTracking';
import {BenchlingState} from 'redux-stores/reduceApp';
import ModalAnchor from 'ui-library/ModalAnchor';

import MatrixInputModal from './MatrixInputModal';
import MoveSummaryModal from './MoveSummaryModal';
import SelectOrphanParentStorableModal from './SelectOrphanParentStorableModal';
import {MODAL_KEYS} from './UploadContainersFlowConstants';

interface UploadContainersFlowProps {
  grid: SampleConstants.Grid;
  onRequestClose: () => void;
}

interface ConnectedProps {
  modalKey: string;
  open: boolean;
}

/**
 * Controls the modal flow logic for upload containers from matrix scanner
 */
export function UploadContainersFlow({
  grid,
  open,
  onRequestClose,
  modalKey,
}: UploadContainersFlowProps & ConnectedProps): JSX.Element {
  return (
    <ModalAnchor open={open}>
      <UploadContainersModal
        grid={grid}
        onRequestClose={onRequestClose}
        modalKey={modalKey}
        title="Set containers from matrix scanner"
      />
    </ModalAnchor>
  );
}

interface UploadContainersModalProps {
  grid: SampleConstants.Grid;
  modalKey: string;
  onRequestClose: () => void;
  title: string;
}

/**
 * Helper component that renders the appropriate modal given the modal key
 */
export function UploadContainersModal({
  grid,
  onRequestClose,
  modalKey,
  title,
}: UploadContainersModalProps): JSX.Element {
  const [canProceedOnSampleStatus, setCanProceedOnSampleStatus] = useState(false);
  const [selectedSampleIds, setSelectedSampleIds] = useState<Array<string>>([]);

  const [shouldShowStatusWarnings, setShouldShowStatusWarnings] = useState(false);
  const [samples, setSamples] = useState<Array<SampleStatusItem> | null>(null);
  const [eventGroupId, setEventGroupId] = useState('');

  useEffect(() => {
    setEventGroupId(Analytics.makeAnalyticsEventId());
  }, []);

  const checkForWarnings = (containers: Array<Container>): void => {
    const {shouldShowWarning, samples} = checkSamplesForStatusWarnings({
      samples: containers,
      showOnlySamplesWithWarnings: true,
    });
    const newSampleIds = samples?.map(({id}) => id) ?? [];
    // Only update (reset override) if different containers are present
    if (!canProceedOnSampleStatus || !isEqual(newSampleIds, selectedSampleIds)) {
      setCanProceedOnSampleStatus(false);
      setShouldShowStatusWarnings(shouldShowWarning);
      setSamples(samples ?? null);
      setSelectedSampleIds(samples?.map(({id}) => id) ?? []);
    }
  };

  const statusWarningTrackingParams: BaseSampleStatusWarningTrackingParams = {
    actionOrigin: ActionOrigin.UI_UNDETERMINED,
    actionAttempted: 'move_location',
    eventGroupId,
  };

  const renderPossibleWarning = (): JSX.Element | null =>
    shouldShowStatusWarnings && samples != null ? (
      <SampleStatusWarning
        sampleIds={samples.map(({id}) => id)}
        selectedSampleIds={selectedSampleIds}
        setSelectedSampleIds={setSelectedSampleIds}
        shouldProceed={canProceedOnSampleStatus}
        setShouldProceed={setCanProceedOnSampleStatus}
        trackingParams={statusWarningTrackingParams}
        samples={samples}
        canSelectMultiple={false}
      />
    ) : null;

  const hasBlockingSampleStatusWarnings = (): boolean =>
    shouldShowStatusWarnings && !canProceedOnSampleStatus;

  switch (modalKey) {
    case MODAL_KEYS.MATRIX_INPUT: {
      return <MatrixInputModal grid={grid} onRequestClose={onRequestClose} title={title} />;
    }
    case MODAL_KEYS.CHANGE_SUMMARY: {
      return (
        <MoveSummaryModal
          onRequestClose={onRequestClose}
          title={title}
          sampleStatusWarningParams={{
            checkForWarnings,
            renderPossibleWarning,
            hasBlockingSampleStatusWarnings,
          }}
        />
      );
    }
    case MODAL_KEYS.SELECT_PARENT: {
      return <SelectOrphanParentStorableModal title={title} onRequestClose={onRequestClose} />;
    }
    default:
      throw new Error(`Unexpected modal key ${modalKey}`);
  }
}

function mapStateToProps(state: BenchlingState): ConnectedProps {
  const {open, modalKey} = state.ui.samples.uploadContainersFlow;
  return {open, modalKey};
}

export default connect(mapStateToProps)(UploadContainersFlow);
